
import { Component, Vue, Watch } from 'vue-property-decorator';
import WebsiteRepositoryInterface from '@/services/Website/interfaces';
import { ServerInterface, ServerRepositoryInterface } from '@/services/Server/Interfaces';
import { ServerStatus } from '@/services/Server';
import { UserInterface, UserRepoInterface } from '@/services/Users/Interfaces';
import { PlanInterface, PlanRepositoryInterface } from '@/services/Plan/interfaces';
import PageHeader from '@/components/layout/PageHeader.vue';
import StatusChip from '@/components/layout/StatusChip.vue';
import {
  QUICK_CREATE_DELAY_MS,
  QUICK_CREATE_DEFAULT_SUFFIX,
  QUICK_CREATE_MAX_COUNT,
  buildBatchDomains,
  generateBatchId,
  normalizeDomainSuffix,
  pickRandomItem,
  pickRandomUpServer,
  sanitizeBatchId,
  sleep,
  sortServersByUpFirst,
  upServers,
} from '@/utils/websiteQuickCreate';

type PreviewStatus = 'queued' | 'creating' | 'done' | 'failed';

interface PreviewRow {
  index: number;
  domain: string;
  originServer: string;
  serverHostname: string;
  userId: string;
  ownerLabel: string;
  planId: number;
  planLabel: string;
  enableEmail: boolean;
  enableWordpress: boolean;
  status: PreviewStatus;
  error: string;
}

@Component({
  components: {
    PageHeader,
    StatusChip,
  },
  inject: [
    'websiteRepository',
    'serverRepository',
    'userRepository',
    'planRepository',
  ],
})
export default class QuickCreateWebsites extends Vue {
  public loading: boolean = true;
  public running: boolean = false;
  public finished: boolean = false;
  public stopRequested: boolean = false;
  public bulkWarningOpen: boolean = false;

  public readonly bulkWarningThreshold: number = 10;

  public websiteRepository!: WebsiteRepositoryInterface;
  public serverRepository!: ServerRepositoryInterface;
  public userRepository!: UserRepoInterface;
  public planRepository!: PlanRepositoryInterface;

  public servers: ServerInterface[] = [];
  public users: UserInterface[] = [];
  public plans: PlanInterface[] = [];

  public batchId: string = generateBatchId();
  public count: number | string = 5;
  public domainSuffix: string = QUICK_CREATE_DEFAULT_SUFFIX;
  public randomServers: boolean = false;
  public originServer: string = '';
  public randomOwners: boolean = false;
  public userId: string = '';
  public randomPlans: boolean = false;
  public planId: number = 1;
  public enableEmail: boolean = true;
  public enableWordpress: boolean = true;

  public rows: PreviewRow[] = [];
  public activeIndex: number = -1;

  public readonly maxCount: number = QUICK_CREATE_MAX_COUNT;
  public readonly countPresets: number[] = [5, 10, 25, 50];

  public headers = [
    { text: '#', value: 'index', width: '56px', sortable: false },
    { text: 'Domain', value: 'domain', sortable: false },
    { text: 'Server', value: 'serverHostname', sortable: false },
    { text: 'Owner', value: 'ownerLabel', sortable: false },
    { text: 'Plan', value: 'planLabel', sortable: false },
    { text: 'Options', value: 'options', sortable: false },
    { text: 'Status', value: 'status', sortable: false },
  ];

  public get formLocked(): boolean {
    return this.running;
  }

  public get canGenerate(): boolean {
    const id = sanitizeBatchId(this.batchId);
    if (!id) {
      return false;
    }
    if (!this.count || this.count < 1 || this.count > QUICK_CREATE_MAX_COUNT) {
      return false;
    }
    if (this.randomServers) {
      if (!upServers(this.servers).length) {
        return false;
      }
    } else if (!this.originServer) {
      return false;
    }
    if (this.randomOwners && !this.users.length) {
      return false;
    }
    if (this.randomPlans) {
      return this.plans.length > 0;
    }
    return Boolean(this.planId);
  }

  public get canStartRun(): boolean {
    return !this.running && this.rows.some((row) => row.status === 'queued');
  }

  public get pendingCreateCount(): number {
    return this.queuedCount || this.rows.length || Number(this.count) || 0;
  }

  public get queuedCount(): number {
    return this.rows.filter((row) => row.status === 'queued').length;
  }

  public get completedCount(): number {
    return this.rows.filter((row) => row.status === 'done' || row.status === 'failed').length;
  }

  public get successCount(): number {
    return this.rows.filter((row) => row.status === 'done').length;
  }

  public get failedCount(): number {
    return this.rows.filter((row) => row.status === 'failed').length;
  }

  public get progressPercent(): number {
    if (!this.rows.length) {
      return 0;
    }
    return Math.round((this.completedCount / this.rows.length) * 100);
  }

  public get progressLabel(): string {
    if (this.finished && !this.running) {
      if (this.rows.some((row) => row.status === 'queued')) {
        return 'Stopped — remaining sites still queued';
      }
      return this.failedCount
        ? 'Batch finished with errors'
        : 'Batch finished successfully';
    }
    if (this.stopRequested) {
      return 'Stopping after current request…';
    }
    if (this.activeIndex >= 0 && this.rows[this.activeIndex]) {
      return `Creating ${this.rows[this.activeIndex].domain}`;
    }
    return 'Creating websites…';
  }

  public async created(): Promise<void> {
    try {
      await this.initialize();
      this.applyDefaults();
      this.syncPreview(true, true, true);
      this.loading = false;
    } catch (err) {
      this.$errorDialog.show((err as any).response?.data?.message || (err as any).message);
    }
  }

  public async initialize(): Promise<void> {
    this.servers = sortServersByUpFirst(await this.serverRepository.getAllServers());
    [this.users] = await this.userRepository.getAllUsers();
    this.plans = await this.planRepository.getAll();
  }

  public applyDefaults(): void {
    const up = pickRandomUpServer(this.servers);
    this.originServer = up ? up._id : (this.servers[0] ? this.servers[0]._id : '');

    const currentUserId = this.$store.getters.id as string;
    const owner = this.users.find((user) => user.id === currentUserId) || this.users[0];
    this.userId = owner ? owner.id : (currentUserId || '');

    const defaultPlan = this.plans.find((plan) => plan.name === 'Default') || this.plans[0];
    this.planId = defaultPlan ? defaultPlan.id : 1;
  }

  @Watch('batchId')
  @Watch('count')
  @Watch('domainSuffix')
  @Watch('randomServers')
  @Watch('originServer')
  @Watch('randomOwners')
  @Watch('userId')
  @Watch('randomPlans')
  @Watch('planId')
  @Watch('enableEmail')
  @Watch('enableWordpress')
  public onSetupChange(): void {
    if (this.loading || this.running) {
      return;
    }
    // Auto-re-roll when the matching random toggle is on.
    this.syncPreview(this.randomServers, this.randomOwners, this.randomPlans);
  }

  public serverLabel(server: ServerInterface): string {
    return `${server.hostname} (${server.status})`;
  }

  public userLabel(user: UserInterface): string {
    return user.email ? `${user.username} · ${user.email}` : user.username;
  }

  public planLabel(plan: PlanInterface): string {
    return plan.name;
  }

  public onBatchIdBlur(): void {
    this.batchId = sanitizeBatchId(this.batchId) || generateBatchId();
  }

  public onCountChange(): void {
    const next = Math.max(1, Math.min(QUICK_CREATE_MAX_COUNT, Math.floor(Number(this.count)) || 1));
    if (this.count !== next) {
      this.count = next;
    }
  }

  public regenerateBatchId(): void {
    this.batchId = generateBatchId();
  }

  public async copyBatchId(): Promise<void> {
    const value = sanitizeBatchId(this.batchId);
    if (!value) {
      return;
    }

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const input = document.createElement('textarea');
        input.value = value;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      }
      this.$successDialog.show('Batch ID copied');
    } catch (err) {
      this.$errorDialog.show('Could not copy batch ID');
    }
  }

  public syncPreview(
    reshuffleServers: boolean = false,
    reshuffleOwners: boolean = false,
    reshufflePlans: boolean = false,
  ): void {
    if (this.running) {
      return;
    }

    if (!this.canGenerate) {
      this.rows = [];
      this.finished = false;
      this.activeIndex = -1;
      return;
    }

    const batchId = sanitizeBatchId(this.batchId) || generateBatchId();
    const domainSuffix = normalizeDomainSuffix(this.domainSuffix);
    const count = Math.max(1, Math.min(QUICK_CREATE_MAX_COUNT, Math.floor(Number(this.count)) || 1));
    const domains = buildBatchDomains(batchId, count, domainSuffix);
    const fallbackOwnerId = this.userId || (this.$store.getters.id as string) || '';

    let serverAssignments: ServerInterface[] = [];
    if (this.randomServers) {
      const pool = upServers(this.servers);
      if (!pool.length) {
        this.rows = [];
        return;
      }

      const previous = this.rows;
      serverAssignments = domains.map((_, i) => {
        if (!reshuffleServers && previous[i] && previous[i].originServer) {
          const existing = this.servers.find((server) => server._id === previous[i].originServer);
          if (existing && existing.status === ServerStatus.Up) {
            return existing;
          }
        }
        return pool[Math.floor(Math.random() * pool.length)];
      });
    } else {
      const selected = this.servers.find((server) => server._id === this.originServer);
      if (!selected) {
        this.rows = [];
        return;
      }
      serverAssignments = domains.map(() => selected);
    }

    let ownerAssignments: UserInterface[] = [];
    if (this.randomOwners) {
      if (!this.users.length) {
        this.rows = [];
        return;
      }

      const previous = this.rows;
      ownerAssignments = domains.map((_, i) => {
        if (!reshuffleOwners && previous[i] && previous[i].userId) {
          const existing = this.users.find((user) => user.id === previous[i].userId);
          if (existing) {
            return existing;
          }
        }
        return pickRandomItem(this.users) as UserInterface;
      });
    } else {
      const selected = this.users.find((user) => user.id === this.userId);
      if (selected) {
        ownerAssignments = domains.map(() => selected);
      } else {
        // Keep a stable fallback owner id even if the user list has no match.
        ownerAssignments = domains.map(() => ({
          id: fallbackOwnerId,
          username: fallbackOwnerId || '—',
          email: '',
        } as UserInterface));
      }
    }

    let planAssignments: PlanInterface[] = [];
    if (this.randomPlans) {
      if (!this.plans.length) {
        this.rows = [];
        return;
      }

      const previous = this.rows;
      planAssignments = domains.map((_, i) => {
        if (!reshufflePlans && previous[i] && previous[i].planId) {
          const existing = this.plans.find((plan) => plan.id === previous[i].planId);
          if (existing) {
            return existing;
          }
        }
        return pickRandomItem(this.plans) as PlanInterface;
      });
    } else {
      const selected = this.plans.find((plan) => plan.id === this.planId);
      if (!selected) {
        this.rows = [];
        return;
      }
      planAssignments = domains.map(() => selected);
    }

    const shapeChanged = this.rows.length !== domains.length
      || this.rows.some((row, i) => !domains[i] || row.domain !== domains[i]);

    this.finished = false;
    this.activeIndex = -1;
    this.rows = domains.map((domain, i) => {
      const server = serverAssignments[i];
      const owner = ownerAssignments[i];
      const plan = planAssignments[i];
      const previous = this.rows[i];
      const keepStatus = !shapeChanged
        && !reshuffleServers
        && !reshuffleOwners
        && !reshufflePlans
        && previous
        && previous.domain === domain
        && previous.originServer === server._id
        && previous.userId === owner.id
        && previous.planId === plan.id;

      return {
        index: i + 1,
        domain,
        originServer: server._id,
        serverHostname: server.hostname,
        userId: owner.id,
        ownerLabel: owner.email || owner.username || owner.id || '—',
        planId: plan.id,
        planLabel: plan.name,
        enableEmail: this.enableEmail,
        enableWordpress: this.enableWordpress,
        status: keepStatus ? previous.status : 'queued' as PreviewStatus,
        error: keepStatus ? previous.error : '',
      };
    });
  }

  public rerollServers(): void {
    if (!this.randomServers || this.formLocked) {
      return;
    }
    this.syncPreview(true, false, false);
  }

  public rerollOwners(): void {
    if (!this.randomOwners || this.formLocked) {
      return;
    }
    this.syncPreview(false, true, false);
  }

  public rerollPlans(): void {
    if (!this.randomPlans || this.formLocked) {
      return;
    }
    this.syncPreview(false, false, true);
  }

  public rowClass(item: PreviewRow): string {
    const classes: string[] = ['quick-create__row'];
    if (item.status === 'creating') {
      classes.push('quick-create__row--active');
    } else if (item.status === 'done') {
      classes.push('quick-create__row--done');
    } else if (item.status === 'failed') {
      classes.push('quick-create__row--failed');
    }
    return classes.join(' ');
  }

  public stopRun(): void {
    this.stopRequested = true;
  }

  public requestStartRun(): void {
    if (!this.canStartRun) {
      return;
    }

    if (this.pendingCreateCount >= this.bulkWarningThreshold) {
      this.bulkWarningOpen = true;
      return;
    }

    this.startRun();
  }

  public confirmBulkCreate(): void {
    this.bulkWarningOpen = false;
    this.startRun();
  }

  public async startRun(): Promise<void> {
    if (!this.canStartRun) {
      return;
    }

    this.running = true;
    this.finished = false;
    this.stopRequested = false;
    this.activeIndex = -1;

    for (let i = 0; i < this.rows.length; i += 1) {
      if (this.stopRequested) {
        break;
      }

      const row = this.rows[i];
      if (row.status === 'done' || row.status === 'failed') {
        continue;
      }

      this.activeIndex = i;
      this.$set(this.rows, i, {
        ...row,
        status: 'creating',
        error: '',
      });

      try {
        await this.websiteRepository.createWebsite(
          row.originServer,
          row.domain,
          row.userId || (this.$store.getters.id as string),
          null,
          this.excludeActionsForRow(row),
          row.planId || 1,
        );
        this.$set(this.rows, i, {
          ...this.rows[i],
          status: 'done',
          error: '',
        });
      } catch (err) {
        const anyErr = err as any;
        const message = anyErr.response?.data?.message || anyErr.message || 'Create failed';
        this.$set(this.rows, i, {
          ...this.rows[i],
          status: 'failed',
          error: message,
        });
      }

      if (i < this.rows.length - 1 && !this.stopRequested) {
        await sleep(QUICK_CREATE_DELAY_MS);
      }
    }

    this.running = false;
    this.finished = true;
    this.activeIndex = -1;
    this.stopRequested = false;
  }

  private excludeActionsForRow(row: PreviewRow): string[] {
    const excluded: string[] = [];
    if (!row.enableEmail) {
      excluded.push('email');
    }
    if (!row.enableWordpress) {
      excluded.push('wp');
    }
    return excluded;
  }
}
